/**
 * Pagina dei rilievi fiscali del motore verifiche (Task V14) - la coda che
 * Walter guarda per decidere: RAGGRUPPATA PER REGOLA (codice), mai per
 * documento (referto dali 01/08, requisito del cantiere).
 *
 * Semantica delle due nozioni di coda esposte dal backend (nota del
 * controllore 2026-08-04, review V13): GET /verifiche/conteggi conta
 * aperto+confermato insieme ("da guardare"); qui invece si sceglie UNA
 * semantica sola e la si tiene coerente in tutta la pagina - la coda di
 * LAVORO sono gli APERTI (tab di default), i CONFERMATI sono un tab
 * separato ("decisi, in attesa di correzione del documento"). Il badge di
 * conteggi.aperti/confermati e' usato SOLO per etichettare le due tab della
 * coda di lavoro, mai sommato in un titolo unico.
 *
 * La sospensione e' un asse SEPARATO dallo stato (sospeso_fino, migration
 * 0020): di default e' esclusa (query param sospesi=false, come fa
 * l'endpoint di default), con uno switch per includerla. Un rilievo
 * sospeso NON e' una coda a se': resta nel suo stato, la sospensione lo
 * segnala soltanto (dettaglio nella pagina per regola, dove si vede la
 * data "in attesa fino al").
 *
 * risolto_automatico e' visibile come tab MA fuori dalla coda di lavoro
 * (separatore visivo dopo Confermati): e' un ESITO, non qualcosa da
 * decidere.
 *
 * Filtro natura_danno: chip/segmenti (5 valori fissi, mai una tendina non
 * cercabile per cosi' pochi valori) con il conteggio di ciascuno, calcolati
 * da UNA sola risposta di GET /verifiche (senza filtro natura_danno lato
 * server): niente richieste aggiuntive al cambio di chip.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
} from '@floraltech/ui';
import { AlertOctagon, AlertTriangle, HelpCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUrlBoolState, useUrlState } from '@lib/useUrlState';
import { useAuth } from '@shell/AuthContext';
import { NATURA_DANNO_ORDINATA, STATI_RILIEVO, etichettaNaturaDanno } from '../lib/verifiche';

interface RilievoGruppo {
  codice: string;
  natura_danno: string;
  norma: string;
  titolo: string;
  conteggio: number;
  /** Ripartizione dello stesso conteggio per gravita' (P3, review V14):
   *  blocchi + attenzioni == conteggio sempre. */
  blocchi: number;
  attenzioni: number;
}

interface Conteggi {
  aperti: number;
  confermati: number;
  totale_da_guardare: number;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const VerificheList: React.FC = () => {
  const { hasPermesso } = useAuth();

  const [stato, setStato] = useUrlState('stato', 'aperto');
  const [sospesi, setSospesi] = useUrlBoolState('sospesi', false);
  const [natura, setNatura] = useUrlState('natura', '__all__');

  const [gruppi, setGruppi] = useState<RilievoGruppo[]>([]);
  const [loading, setLoading] = useState(true);
  // Distingue il primo caricamento (schermo vuoto, va bene un placeholder)
  // dai refetch successivi: la tabella con i dati della vista precedente
  // NON deve smontarsi mentre arriva la risposta nuova.
  const [ricaricando, setRicaricando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [conteggi, setConteggi] = useState<Conteggi | null>(null);

  // Identifica la richiesta corrente: incrementata a ogni cambio tab/switch,
  // per scartare una risposta tardiva di una richiesta precedente (due click
  // rapidi su due tab possono farla atterrare per ultima e mostrare la lista
  // sbagliata per il filtro ormai attivo - dali, review V14). Stesso pattern
  // di CambiaIntestatarioDialog.tsx.
  const richiestaGenerazioneRef = useRef(0);

  const caricaGruppi = useCallback(async () => {
    richiestaGenerazioneRef.current += 1;
    const generazione = richiestaGenerazioneRef.current;
    setRicaricando(true);
    setError(null);
    try {
      const params = new URLSearchParams({ stato, sospesi: sospesi ? 'true' : 'false' });
      const res = await fetch(`/api/v1/verifiche?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (richiestaGenerazioneRef.current !== generazione) return;
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Non hai il permesso di visualizzare i rilievi fiscali.');
        }
        throw new Error(`Errore ${res.status}`);
      }
      setGruppi((await res.json()) as RilievoGruppo[]);
    } catch (err) {
      if (richiestaGenerazioneRef.current === generazione) {
        setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      }
    } finally {
      if (richiestaGenerazioneRef.current === generazione) {
        setLoading(false);
        setRicaricando(false);
      }
    }
  }, [stato, sospesi]);

  useEffect(() => {
    void caricaGruppi();
  }, [caricaGruppi]);

  // Conteggi aperti/confermati per le etichette delle tab della coda di
  // lavoro. Rappresentano SEMPRE i rilievi non sospesi (l'endpoint non ha un
  // parametro sospesi): con lo switch "Includi sospesi" attivo il numero
  // smetterebbe di corrispondere a cio' che si vede in tabella, quindi non
  // si mostra affatto in quel caso (mai un numero che non torna).
  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch('/api/v1/verifiche/conteggi', { headers: authHeaders() });
        if (res.ok) setConteggi((await res.json()) as Conteggi);
      } catch {
        /* Il badge sulle tab e' un aiuto, non un dato critico: se non arriva
           le tab restano comunque etichettate e utilizzabili. */
      }
    })();
  }, []);

  // Conteggio per natura_danno calcolato dai gruppi GIA' caricati (nessuna
  // richiesta aggiuntiva al cambio di chip).
  const conteggioPerNatura = useMemo(() => {
    const mappa = new Map<string, number>();
    for (const g of gruppi) mappa.set(g.natura_danno, (mappa.get(g.natura_danno) ?? 0) + g.conteggio);
    return mappa;
  }, [gruppi]);

  const gruppiFiltrati = useMemo(
    () => (natura === '__all__' ? gruppi : gruppi.filter((g) => g.natura_danno === natura)),
    [gruppi, natura],
  );

  const totaleVisibile = gruppiFiltrati.reduce((s, g) => s + g.conteggio, 0);

  const statoCorrente = STATI_RILIEVO.find((s) => s.codice === stato);
  const codaGruppo = STATI_RILIEVO.filter((s) => s.gruppo === 'coda');
  const archivioGruppo = STATI_RILIEVO.filter((s) => s.gruppo === 'archivio');

  const badgeTab = (codice: string): number | null => {
    if (sospesi) return null;
    if (codice === 'aperto') return conteggi?.aperti ?? null;
    if (codice === 'confermato') return conteggi?.confermati ?? null;
    return null;
  };

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
            Rilievi fiscali
          </h1>
          <p className="text-sm text-muted-foreground">
            {loading
              ? 'Caricamento...'
              : `${totaleVisibile} rilievi ${statoCorrente?.etichetta.toLowerCase() ?? stato} in ${gruppiFiltrati.length} regole`}
          </p>
        </div>
        <Tooltip label="Ogni riga e' una REGOLA del motore verifiche, non un documento: il conteggio e' quanti documenti hanno un rilievo aperto su quella regola. La sospensione (asse separato dallo stato) si vede e si gestisce entrando nella regola.">
          <button
            type="button"
            aria-label="Informazioni sui rilievi fiscali"
            className="text-muted-foreground hover:text-foreground"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </Tooltip>
      </div>

      {/* Tab di stato: coda di lavoro (aperto/confermato) separata
          visivamente dall'archivio (archiviato/risolto_automatico) - sono
          due cose diverse, non quattro varianti della stessa lista. */}
      <Tabs value={stato} onValueChange={setStato}>
        <TabsList>
          {codaGruppo.map((s) => {
            const badge = badgeTab(s.codice);
            return (
              <TabsTrigger key={s.codice} value={s.codice}>
                {s.etichetta}
                {badge !== null && <span className="ml-1.5 text-xs text-muted-foreground">({badge})</span>}
              </TabsTrigger>
            );
          })}
          <span aria-hidden="true" className="mx-1 h-4 w-px self-center bg-border" />
          {archivioGruppo.map((s) => (
            <TabsTrigger key={s.codice} value={s.codice}>
              {s.etichetta}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Filtri: natura_danno a chip (5 valori fissi) + switch sospesi */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Natura del danno</span>
            <button
              type="button"
              onClick={() => setNatura('__all__')}
              aria-pressed={natura === '__all__'}
              className={
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
                (natura === '__all__'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-foreground hover:bg-muted')
              }
            >
              Tutte ({gruppi.reduce((s, g) => s + g.conteggio, 0)})
            </button>
            {NATURA_DANNO_ORDINATA.map((n) => {
              const count = conteggioPerNatura.get(n.codice) ?? 0;
              const attivo = natura === n.codice;
              // A zero il chip si nasconde SALVO se e' quello attivo: cambiando
              // tab un filtro gia' selezionato non deve sparire lasciando solo
              // il messaggio di lista vuota a spiegare perche' (dali, review V14).
              if (count === 0 && !attivo) return null;
              return (
                <button
                  key={n.codice}
                  type="button"
                  onClick={() => setNatura(attivo ? '__all__' : n.codice)}
                  aria-pressed={attivo}
                  className={
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
                    (attivo
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-foreground hover:bg-muted')
                  }
                >
                  {n.etichetta} ({count})
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2 text-sm">
            <input
              id="verifiche-includi-sospesi"
              type="checkbox"
              checked={sospesi}
              onChange={(e) => setSospesi(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            <label htmlFor="verifiche-includi-sospesi">Includi sospesi</label>
            <Tooltip label="Un rilievo sospeso resta nel suo stato: il motore non lo tocca finche' non passa la data di sospensione. Di default e' escluso dalla coda; attiva questo switch per vederlo comunque, con l'indicazione di fino a quando.">
              <button
                type="button"
                aria-label="Cosa significa sospeso"
                className="text-muted-foreground hover:text-foreground"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        </CardContent>
      </Card>

      {/* Tabella dei gruppi. L'errore NON smonta la tabella (dali, review
          V14): banner sopra, righe della vista precedente restano a
          schermo - stesso trattamento del dettaglio per regola. */}
      <Card>
        <CardContent className="p-0">
          {error && (
            <div
              role="alert"
              className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4 text-sm"
            >
              <span className="text-destructive">{error}</span>
              <Button variant="outline" size="sm" onClick={() => void caricaGruppi()}>
                Riprova
              </Button>
            </div>
          )}
          <Table aria-busy={ricaricando} className={ricaricando ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <TableCaption>Ordinate per gravita' del danno (CATALOGO 0.4).</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Regola</TableHead>
                <TableHead>Natura del danno</TableHead>
                <TableHead>Gravita'</TableHead>
                <TableHead className="text-right">Rilievi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && !ricaricando && !error && gruppiFiltrati.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                    {natura !== '__all__'
                      ? 'Nessun rilievo con questa natura del danno in questo stato.'
                      : `Nessun rilievo ${statoCorrente?.etichetta.toLowerCase() ?? stato}.`}
                  </TableCell>
                </TableRow>
              )}
              {gruppiFiltrati.map((g) => (
                <TableRow key={g.codice}>
                  <TableCell className="p-0">
                    {/* Link vero (non un onClick nudo sul <tr>): raggiungibile
                        da tastiera e apribile con ctrl/cmd+click senza
                        perdere la coda (dali, review V14). */}
                    <Link
                      to={`/documentale/verifiche/${encodeURIComponent(g.codice)}?stato=${stato}&sospesi=${sospesi ? '1' : '0'}`}
                      className="block rounded p-3 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
                    >
                      <div className="font-medium text-foreground">{g.titolo}</div>
                      <div className="text-xs text-muted-foreground">{g.norma}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{g.codice}</div>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{etichettaNaturaDanno(g.natura_danno)}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {g.blocchi > 0 && (
                        <Badge variant="destructive" className="whitespace-nowrap">
                          <AlertOctagon className="mr-1 h-3 w-3" />
                          {g.blocchi}
                        </Badge>
                      )}
                      {g.attenzioni > 0 && (
                        <Badge variant="warning" className="whitespace-nowrap">
                          <AlertTriangle className="mr-1 h-3 w-3" />
                          {g.attenzioni}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-base font-semibold tabular-nums">
                    {g.conteggio}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!hasPermesso('documentale.modifica') && (
        <p className="text-xs text-muted-foreground">
          Puoi vedere i rilievi ma non decidere su di essi: manca il permesso di modifica del
          modulo documentale.
        </p>
      )}
    </div>
  );
};

export default VerificheList;
